import { YesNo } from '@zorro/types';
import { Grid, LabelWithTooltip, Stack } from '@zorro/web/design-system';
import { useEffect } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';

import { useAnalytics } from '../../Analytics';
import { AgentOrEmployeeInput } from './AgentOrEmployeeInput';
import { MonthlyAutoPayDateInput } from './MonthlyAutoPayDateInput';
import { PaymentStatusRadioCards } from './PaymentStatusRadioCards';
import { PaymentStatusFormInput } from './PaymentStatusUtils';

type Props = {
  paymentStatusForm: UseFormReturn<PaymentStatusFormInput>;
  onboardingPeriodId: string;
  shouldShowCompleted?: boolean;
  isFinalizationMode?: boolean;
  isDisabled?: boolean;
};

// The shared payment-status block: initial payment and auto-pay always travel
// together (who-handles + status), plus the monthly date when auto-pay is set
// up. Consumed by PaymentStatusSection (titled) and directly by
// ManagePaymentStatus and the ops finalization pages.
export const PaymentStatusFormFields = ({
  paymentStatusForm,
  onboardingPeriodId,
  shouldShowCompleted,
  isFinalizationMode = false,
  isDisabled,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const { control, setValue } = paymentStatusForm;

  const onInitialPaymentSelect = async (value: string) => {
    await sendEvent('select_initial_payment_completed', {
      onboarding_period_id: onboardingPeriodId,
      status: value.toLocaleLowerCase(),
    });
  };

  const autoPayCompleted = useWatch({ control, name: 'autoPayCompleted' });
  const shouldShowMonthlyAutoPayDate = autoPayCompleted === YesNo.YES;

  useEffect(() => {
    if (!shouldShowMonthlyAutoPayDate) {
      setValue('monthlyAutoPayDate', null);
    }
  }, [shouldShowMonthlyAutoPayDate, setValue]);

  return (
    <Stack gap="xxl">
      <Grid>
        <Grid.Col span={12}>
          <AgentOrEmployeeInput
            name="initialPremiumHandler"
            control={control}
            isDisabled={isDisabled}
            label="Initial payment"
            description={
              <LabelWithTooltip
                label="Who handles the payment?"
                tooltipLabel="Typically, you would handle the initial payment as the enrollment team agent. The employee handles the initial payment if you helped them enroll independently on an exchange, or if the carrier does not allow you to submit an initial payment as the Agent"
                isRequired={isFinalizationMode}
                color="var(--color-text-default-secondary)"
              />
            }
            onSendEvent={async (value) => {
              await sendEvent('select_initial_payment_handling', {
                onboarding_period_id: onboardingPeriodId,
                persona: value.toLocaleLowerCase(),
              });
            }}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <PaymentStatusRadioCards
            field="initialPayment"
            shouldShowCompleted={Boolean(shouldShowCompleted)}
            name="initialPremiumCompleted"
            control={control}
            isRequired={isFinalizationMode}
            isDisabled={isDisabled}
            onSendEvent={onInitialPaymentSelect}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={12}>
          <AgentOrEmployeeInput
            name="autoPaySetter"
            control={control}
            isDisabled={isDisabled}
            label="Auto-pay"
            description={
              <LabelWithTooltip
                label="Who handles setting up auto-pay?"
                tooltipLabel="If the carrier does not allow you to set up auto-pay as the agent, indicate that the employee handles this step"
                isRequired={isFinalizationMode}
                color="var(--color-text-default-secondary)"
              />
            }
            onSendEvent={async (value) => {
              await sendEvent('select_autopay_handling', {
                onboarding_period_id: onboardingPeriodId,
                persona: value.toLocaleLowerCase(),
              });
            }}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <PaymentStatusRadioCards
            field="autoPay"
            name="autoPayCompleted"
            control={control}
            isRequired={isFinalizationMode}
            isDisabled={isDisabled}
            onSendEvent={async (value) => {
              await sendEvent('select_autopay_has_set_up', {
                onboarding_period_id: onboardingPeriodId,
                status: value.toLocaleLowerCase(),
              });
            }}
          />
        </Grid.Col>
        {shouldShowMonthlyAutoPayDate && (
          <Grid.Col span={6}>
            <MonthlyAutoPayDateInput
              name="monthlyAutoPayDate"
              control={control}
              isRequired={isFinalizationMode}
              isDisabled={isDisabled}
            />
          </Grid.Col>
        )}
      </Grid>
    </Stack>
  );
};
